/**
 * An assessment's paper sections and their targets, read from its blueprint.
 *
 * A blueprint allocates questions to skills under a few content domains and to
 * Bloom's levels. The paper gets one section per domain, in blueprint order,
 * and each section's plan is that domain plus the blueprint's Bloom's levels.
 * Leo's first draft fills these sections, and the Blueprint column checks
 * against the same plan, so what Leo places reads Aligned.
 *
 * Only for a blueprint the author picked when creating the assessment. The
 * demo papers were built around their course's sections and keep them, and a
 * blueprint seeded later (the demo exam plan) did not shape the paper either.
 * Without one, callers keep their course-based sections.
 */

import { blueprintStore } from "@/lib/blueprint/store"
import { DEFAULT_EXAM_CATEGORIES } from "@/lib/exam-categories/defaults"
import { getRootCategoryId } from "@/lib/exam-categories/tree"
import { labelForContentCategory } from "@/lib/exam-categories/resolve"
import { createPersistedStore } from "@/lib/persisted-store"

/** Assessments whose blueprint was picked in Create assessment. */
const chosenAtCreateStore = createPersistedStore<string[]>(
  "exam:blueprint-chosen-at-create:v1",
  () => [],
)

export function rememberBlueprintChosenAtCreate(assessmentId: string): void {
  chosenAtCreateStore.set((ids) => (ids.includes(assessmentId) ? ids : [...ids, assessmentId]))
}

export type BlueprintSectionDomain = {
  /** Section id on the paper: A, B, C… */
  sectionId: string
  /** Root content category the section covers. */
  categoryId: string
  label: string
  /** Questions the blueprint allocates to this domain. */
  questions: number
}

export type BlueprintSectionPlan = {
  topics: string[]
  /** Bloom's levels in the paper's vocabulary ("Applying"). */
  blooms: string[]
}

/** Bloom's task id ("applying") as the paper writes it ("Applying"). */
function paperBloom(taskId: string): string {
  return taskId.charAt(0).toUpperCase() + taskId.slice(1)
}

export function blueprintSectionDomains(
  assessmentId: string | undefined,
): BlueprintSectionDomain[] {
  if (!assessmentId || !chosenAtCreateStore.get().includes(assessmentId)) return []
  const blueprint = blueprintStore.get()[assessmentId]
  if (!blueprint) return []
  const byRoot = new Map<string, number>()
  for (const allocation of blueprint.categoryAllocations ?? []) {
    if (!(allocation.questions > 0)) continue
    const root =
      getRootCategoryId(DEFAULT_EXAM_CATEGORIES, allocation.categoryId) ??
      allocation.categoryId
    byRoot.set(root, (byRoot.get(root) ?? 0) + allocation.questions)
  }
  return [...byRoot].map(([categoryId, questions], index) => ({
    sectionId: String.fromCharCode(65 + index),
    categoryId,
    label: labelForContentCategory(categoryId),
    questions,
  }))
}

/** Bloom's levels the blueprint asks for, most questions first. */
export function blueprintBloomTargets(
  assessmentId: string | undefined,
): { bloom: string; questions: number }[] {
  if (!assessmentId) return []
  const blueprint = blueprintStore.get()[assessmentId]
  return (blueprint?.cognitiveAllocations ?? [])
    .filter((allocation) => allocation.questions > 0)
    .sort((a, b) => b.questions - a.questions)
    .map((allocation) => ({ bloom: paperBloom(allocation.id), questions: allocation.questions }))
}

/*
 * The paper on screen. The Blueprint column, the preview sheet and the gap
 * banners ask for a section's plan by section id alone, so the open assessment
 * registers its blueprint here and they read it without threading an id
 * through every caller.
 */
let activePlans: { assessmentId: string; plans: Map<string, BlueprintSectionPlan> } | null = null

/** Point section plans at this assessment's blueprint. Cheap to call on every render. */
export function activateBlueprintSectionPlans(assessmentId: string | undefined): void {
  if (!assessmentId) {
    activePlans = null
    return
  }
  const domains = blueprintSectionDomains(assessmentId)
  if (domains.length === 0) {
    activePlans = null
    return
  }
  const blooms = blueprintBloomTargets(assessmentId).map((target) => target.bloom)
  activePlans = {
    assessmentId,
    plans: new Map(
      domains.map((domain) => [domain.sectionId, { topics: [domain.label], blooms }]),
    ),
  }
}

/** The open assessment's blueprint plan for a section, or null when it has no blueprint. */
export function activeBlueprintSectionPlan(sectionId: string): BlueprintSectionPlan | null {
  return activePlans?.plans.get(sectionId) ?? null
}
